
import { useEffect, useState, useCallback, useMemo } from "react";
import { completePageNavigation, markContentRendered } from "@/utils/a2a/monitoring/performanceMetrics";

// Create a consistent context interface
export interface OrchestrationContextState {
  isLoading: boolean;
  error: Error | null;
  initialized: boolean;
}

export const useOrchestrationContext = () => {
  const [state, setState] = useState<OrchestrationContextState>({
    isLoading: true,
    error: null,
    initialized: false,
  });

  // Memoize state changes to avoid unnecessary re-renders
  const setLoading = useCallback((isLoading: boolean) => {
    setState(prevState => ({ ...prevState, isLoading }));
    
    // Track page loading metrics
    if (!isLoading) {
      markContentRendered();
      completePageNavigation();
    }
  }, []);

  const setError = useCallback((error: Error | null) => {
    setState(prevState => ({ ...prevState, error }));
  }, []);

  const setInitialized = useCallback((initialized: boolean) => {
    setState(prevState => ({ ...prevState, initialized }));
  }, []);

  // Mark as initialized on first load
  useEffect(() => {
    if (!state.initialized) {
      setInitialized(true);
    }
  }, [state.initialized, setInitialized]);

  // For components to easily get state without re-renders
  const contextValue = useMemo(() => ({
    ...state,
    setLoading,
    setError,
    setInitialized,
  }), [state, setLoading, setError, setInitialized]);

  return contextValue;
};
